// ── Entity History (/entity/history) ───────────────────────────────────────
// Pick one company (customer, supplier, broker, lab or service) and see
// everything done with them, newest first, with Type and Date filters and
// a search; then the stones currently out with them on memo. Export CSV or
// Print. The data comes from utils/entityHistory.ts. ?id=<entityId> opens a
// company straight away (the Entity popup's History button uses it).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Dropdown, EntityTypeBadge, Paging } from '../components/ui';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { InvoicePreviewModal } from './Purchase';
import { SalePreviewModal } from './Sales';
import { EntityCombobox } from '../components/ui/EntityCombobox';
import { DateRangeTabs, HeaderSearch, IconExportSm, StatChips, type StatChip } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { canAccessPage } from '../utils/pages';
import { fmtMoney } from '../utils/currency';
import { downloadCSV } from '../utils/csv';
import { computeDateWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  loadEntityHistory, HISTORY_KIND, invoiceOutstanding, historySide, compareNewest,
  type EntityHistoryData, type HistoryRow, type OpenMemoRow,
} from '../utils/entityHistory';
import type { Entity, PurchaseInvoice, SaleInvoice } from '../types';

// Excel-style filter on the Type column (tick several kinds).
const TYPE_ORDER = Object.values(HISTORY_KIND).map(k => k.label);
// Date is filtered by month ("Oct 2026"), newest first.
const monthOf = (d: string) => d ? new Date(`${d.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '';
const byMonthDesc = (a: string, b: string) => Date.parse(`1 ${b}`) - Date.parse(`1 ${a}`);
const HISTORY_FILTERS: FilterColumn<HistoryRow>[] = [
  { key: 'date', label: 'Date', get: r => monthOf(r.date), sort: byMonthDesc },
  { key: 'type', label: 'Type', get: r => HISTORY_KIND[r.kind].label,
    sort: (a, b) => TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b) },
  { key: 'invoice', label: 'Invoice No', get: r => (r.ref ? shortInvoiceNo(r.ref) : '') },
];
const MEMO_FILTERS: FilterColumn<OpenMemoRow>[] = [
  { key: 'code',  label: 'Code',  get: m => m.code },
  { key: 'stone', label: 'Stone', get: m => m.stone },
  { key: 'returnBy', label: 'Return by', get: m => {
    if (!m.returnBy) return '';
    const t = new Date(); const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    return m.returnBy < today ? 'Late' : 'Not due yet';
  } },
];

const shortDate = (d: string) => d
  ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function RefPill({ value }: { value: string }) {
  if (!value) return <span style={{ opacity: 0.4 }}>—</span>;
  return (
    <span title={value} style={{
      background: 'var(--primary-light)', color: 'var(--primary)', fontFamily: 'var(--font-num)',
      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>{value.length > 8 ? value.slice(-4) : value}</span>
  );
}

function KindPill({ row }: { row: HistoryRow }) {
  const k = HISTORY_KIND[row.kind];
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', color: k.color,
      background: `color-mix(in srgb, ${k.color} 10%, transparent)`,
      border: `1px solid color-mix(in srgb, ${k.color} 30%, transparent)`,
    }}>{k.label}</span>
  );
}

function IconEmptyHistory() {
  return <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

const DEFAULT_RANGE: DateFilterKey = '3-months';

export default function EntityHistoryPage() {
  const { user } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency ?? 'USD');
  const [params, setParams] = useSearchParams();

  const [entities, setEntities] = useState<Entity[]>([]);
  const entityId = params.get('id') ?? '';
  const entity = entities.find(e => e.id === entityId) ?? null;

  const [data,    setData]    = useState<EntityHistoryData | null>(null);
  const [loadedFor, setLoadedFor] = useState('');
  const [error,   setError]   = useState('');
  // Opens on the last 3 months; Clear goes back to that.
  const [dateKey, setDateKey] = useState<DateFilterKey>(DEFAULT_RANGE);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [search,  setSearch]  = useState('');
  // The invoice opened from an Invoice No (the same popup as Sales / Purchase, read only).
  const [openSale, setOpenSale] = useState<SaleInvoice | null>(null);
  const [openBuy,  setOpenBuy]  = useState<PurchaseInvoice | null>(null);
  const [opening,  setOpening]  = useState('');

  // Companies for the picker, once.
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    getDocs(collection(db, 'entities')).then(snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))
        .map(e => ({ ...e, types: e.types ?? [], companyName: e.companyName ?? '' }))
        .sort((a, b) => a.companyName.localeCompare(b.companyName));
      setEntities(list);
    }).catch(() => setError('Could not load the companies.'));
  }, []);

  const access = useMemo(() => ({
    sales:     canAccessPage(user, 'sales'),
    purchases: canAccessPage(user, 'purchase'),
    memos:     canAccessPage(user, 'memos'),
    brokers:   canAccessPage(user, 'brokers'),
    expenses:  canAccessPage(user, 'expenses') || canAccessPage(user, 'purchase-expenses') || canAccessPage(user, 'sales-expenses'),
  }), [user]);

  // Everything for the picked company, once per pick.
  useEffect(() => {
    const db = getClientDb();
    if (!db || !entity) return;
    let cancelled = false;
    loadEntityHistory(db, entity, access)
      .then(d => { if (!cancelled) { setData(d); setLoadedFor(entity.id); setError(''); } })
      .catch(e => {
        console.error('[Entity history] load failed:', e);
        if (!cancelled) { setError('Could not load the history. Check your connection and try again.'); setLoadedFor(entity.id); }
      });
    return () => { cancelled = true; };
  }, [entity, access]);

  const loading = !!entity && loadedFor !== entity.id;

  function pick(id: string) {
    colFilters.clear(); setSearch('');
    setParams(id ? { id } : {}, { replace: true });
  }

  const window_ = computeDateWindow(dateKey, customFrom, customTo);
  const inWindow = (d: string) => !window_ || ((!window_.from || d >= window_.from) && (!window_.to || d < window_.to));

  const allRows = useMemo(() => (data && entity && loadedFor === entity.id ? data.rows : []), [data, entity, loadedFor]);
  const dated = allRows.filter(r => inWindow(r.date));
  const q = search.trim().toLowerCase();
  const searched = dated.filter(r =>
    !q || r.ref.toLowerCase().includes(q) || r.detail.toLowerCase().includes(q) || HISTORY_KIND[r.kind].label.toLowerCase().includes(q));
  const { rows: filtered, filters: colFilters } = useColumnFilters(searched, HISTORY_FILTERS);
  // Newest first by default; clicking the Date heading flips it.
  const [dateDesc, setDateDesc] = useState(true);
  const shown = useMemo(() => {
    const out = [...filtered].sort(compareNewest);
    return dateDesc ? out : out.reverse();
  }, [filtered, dateDesc]);

  // Figures: totals for the chosen dates, and what's owed right now.
  const chips: StatChip[] = useMemo(() => {
    if (!data || !entity || loadedFor !== entity.id) return [];
    const sum = (kinds: string[]) => dated.filter(r => kinds.includes(r.kind)).reduce((s, r) => s + r.amount, 0);
    const has = (kinds: string[]) => allRows.some(r => kinds.includes(r.kind));
    const out: StatChip[] = [];
    if (has(['sale'])) {
      out.push({ label: 'Sold', value: money(sum(['sale'])) });
      out.push({ label: 'Received', value: money(sum(['received', 'adv-in'])) });
      const due = data.sales.reduce((s, i) => s + invoiceOutstanding(i), 0);
      out.push({ label: 'To collect now', value: due > 0.005 ? money(due) : 'Nothing', tone: due > 0.005 ? 'danger' : 'success' });
    }
    // How much of the customer's credit limit is used by what they owe now.
    const limit = entity.creditLimitUSD ?? 0;
    if (limit > 0) {
      const due = data.sales.reduce((s, i) => s + invoiceOutstanding(i), 0);
      const pct = (due / limit) * 100;
      const tone = pct > 100 ? 'danger' : pct >= 75 ? 'warning' : 'success';
      out.push({
        label: 'Credit limit',
        value: (
          <span className="eh-credit" title={`${money(due)} owed of a ${money(limit)} limit`}>
            <span className="eh-credit-bar"><span style={{ width: `${Math.min(100, pct)}%`, background: `var(--${tone})` }} /></span>
            <span>{money(limit)}</span>
            <span className="tb-stat-note" style={{ color: `var(--${tone})` }}>{Math.round(pct)}% used</span>
          </span>
        ),
      });
    }
    if (has(['purchase'])) {
      out.push({ label: 'Bought', value: money(sum(['purchase'])) });
      out.push({ label: 'Paid', value: money(sum(['paid', 'adv-out'])) });
      const due = data.purchases.reduce((s, i) => s + invoiceOutstanding(i), 0);
      out.push({ label: 'To pay now', value: due > 0.005 ? money(due) : 'Nothing', tone: due > 0.005 ? 'danger' : 'success' });
    }
    if (has(['adv-in'])) {
      out.push({ label: 'Their advance unused', value: data.advanceHeld > 0.005 ? money(data.advanceHeld) : 'Nothing', tone: data.advanceHeld > 0.005 ? 'primary' : undefined });
    }
    if (has(['adv-out'])) {
      out.push({ label: 'Our advance unused', value: data.advanceGiven > 0.005 ? money(data.advanceGiven) : 'Nothing', tone: data.advanceGiven > 0.005 ? 'warning' : undefined });
    }
    if (has(['commission'])) {
      out.push({ label: 'Commission', value: money(sum(['commission'])) });
      out.push({ label: 'Commission due', value: data.commissionDue > 0.005 ? money(data.commissionDue) : 'Nothing', tone: data.commissionDue > 0.005 ? 'danger' : 'success' });
    }
    if (has(['bill'])) {
      out.push({ label: 'Bills', value: money(sum(['bill'])) });
      out.push({ label: 'Bills unpaid', value: data.billsDue > 0.005 ? money(data.billsDue) : 'Nothing', tone: data.billsDue > 0.005 ? 'danger' : 'success' });
    }
    if (data.memos.length) out.push({ label: 'On memo now', value: `${data.memos.reduce((s, m) => s + m.carat, 0).toFixed(2)} ct`, tone: 'primary' });
    return out;
  }, [data, entity, loadedFor, dated, allRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const allMemos = useMemo(() => (data && entity && loadedFor === entity.id ? data.memos : []), [data, entity, loadedFor]);
  const { rows: memoRows, filters: memoFilters } = useColumnFilters(allMemos, MEMO_FILTERS);
  const memos = useMemo(() => [...memoRows].sort((a, b) => b.date.localeCompare(a.date)), [memoRows]);

  // 50 rows at a time; a new company, filter or search goes back to page 1.
  const pagingKey = [entityId, colFilters.stamp, dateKey, customFrom, customTo, q, dateDesc].join('|');
  const hist = usePaging(shown, pagingKey);
  const memoPg = usePaging(memos, `${entityId}|${memoFilters.stamp}`);

  // Two tabs (History / Memo Out: open); each table scrolls inside a box
  // that ends just above the page numbers, header and Total row pinned.
  const [view, setView] = useState<'history' | 'memos'>('history');
  const tab = view === 'memos' && access.memos ? 'memos' : 'history';
  const fitRef = useRef<HTMLDivElement>(null);
  useFitHeight(fitRef, 56, 240, [tab, shown.length > 0, memos.length > 0, loading, entityId]);

  // Totals of every row matching the filters (all pages, not just this one).
  const totals = useMemo(() => {
    let invoiced = 0, paid = 0;
    for (const r of shown) {
      const side = historySide(r.kind);
      if (side.col === 'invoiced') invoiced += side.sign * r.amount;
      else if (side.col === 'paid') paid += r.amount;
    }
    return { invoiced, paid };
  }, [shown]);

  const canOpen = (r: HistoryRow) => !!r.invoice && canAccessPage(user, r.invoice.coll === 'sales' ? 'sales' : 'purchase');
  async function openInvoice(r: HistoryRow) {
    const db = getClientDb();
    if (!db || !r.invoice) return;
    setOpening(r.key);
    try {
      const snap = await getDoc(doc(db, r.invoice.coll, r.invoice.id));
      if (!snap.exists()) { setError('That invoice no longer exists.'); return; }
      if (r.invoice.coll === 'sales') setOpenSale({ id: snap.id, ...snap.data() } as SaleInvoice);
      else setOpenBuy({ id: snap.id, ...snap.data() } as PurchaseInvoice);
    } catch {
      setError('Could not open that invoice.');
    } finally {
      setOpening('');
    }
  }
  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const today = todayISO();
  const narrowed = colFilters.active.length > 0 || dateKey !== DEFAULT_RANGE || !!q;

  function exportCSV() {
    if (!entity) return;
    const slug = entity.companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'company';
    downloadCSV(`history-${slug}`, ['Date', 'Type', 'Invoice No', 'Details', 'Invoiced', 'Paid / Received'],
      shown.map(r => {
        const side = historySide(r.kind);
        const v = (side.sign * r.amount).toFixed(2);
        return [r.date, HISTORY_KIND[r.kind].label, r.ref, r.detail, side.col === 'invoiced' ? v : '', side.col === 'paid' ? v : ''];
      }));
  }

  const th = { position: 'sticky' as const, top: 0, zIndex: 1, whiteSpace: 'nowrap' as const };
  const td = { fontSize: 12, color: 'var(--text-2)' };
  const num = { ...td, fontFamily: 'var(--font-num)', textAlign: 'right' as const, whiteSpace: 'nowrap' as const };
  const muted = <span style={{ opacity: 0.4 }}>—</span>;


  return (
    <AppLayout
      title="Entity History"
      subtitle="Everything done with one company"
      toolbar={<>
        <div className="form-group tb-combo" style={{ width: 260 }}>
          <EntityCombobox entities={entities} value={entityId} onChange={pick} placeholder="Pick a company…" />
        </div>
        {entity && <>
          <DateRangeTabs value={dateKey} onChange={setDateKey}
            customFrom={customFrom} customTo={customTo} onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} />
          <HeaderSearch value={search} onChange={setSearch} placeholder="Search no or details" width={200} />
          {narrowed && (
            <button type="button" className="tb-clear" onClick={() => { colFilters.clear(); setDateKey(DEFAULT_RANGE); setSearch(''); }}>Clear</button>
          )}
        </>}
      </>}
      headerActions={entity && (
        <div className="tb-group">
          <Dropdown align="right" trigger={() => (
            <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
          )}>
            <button type="button" className="dropdown-item" onClick={exportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => globalThis.print()}>Print</button>
          </Dropdown>
        </div>
      )}
    >
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!entity ? (
        <div className="card" style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
          <IconEmptyHistory />
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginTop: 10 }}>Pick a company</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            Choose a customer, supplier, broker or service at the top to see everything done with them.
          </div>
        </div>
      ) : loading ? (
        <div className="card" style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Loading history…</div>
      ) : (
        <>
          {/* Who this is, then the figures. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)' }}>{entity.companyName}</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {entity.types.map(t => <EntityTypeBadge key={t} type={t} />)}
            </div>
          </div>
          {chips.length > 0 && <StatChips items={chips} />}

          <div style={{ height: 14 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div className="tb-seg" role="tablist" aria-label="Show">
              <button type="button" role="tab" aria-selected={tab === 'history'} className={tab === 'history' ? 'on' : undefined}
                onClick={() => setView('history')}>History ({shown.length})</button>
              {access.memos && (
                <button type="button" role="tab" aria-selected={tab === 'memos'} className={tab === 'memos' ? 'on' : undefined}
                  onClick={() => setView('memos')}>Memo Out: open ({memos.length})</button>
              )}
            </div>
            <ColumnFilterChips filters={tab === 'memos' ? memoFilters : colFilters} inline />
          </div>

          {tab === 'history' ? (shown.length === 0 ? (
            <div className="card" style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              {allRows.length === 0 ? 'Nothing done with this company yet.' : 'Nothing matches these filters.'}
            </div>
          ) : (
            <>
              <div ref={fitRef} className="table-wrap table-fit table-fit-stats">
                <table>
                  <thead>
                    <tr>
                      <th style={{ ...th, cursor: 'pointer', userSelect: 'none' }} className="th-filterable" onClick={() => setDateDesc(d => !d)}
                        title={dateDesc ? 'Newest first. Click for oldest first.' : 'Oldest first. Click for newest first.'}>
                        Date <span style={{ fontSize: 10, color: 'var(--primary)' }}>{dateDesc ? '▼' : '▲'}</span>
                        <ColumnFilterButton filters={colFilters} colKey="date" label="Date" />
                      </th>
                      <th style={th} className="th-filterable">Type<ColumnFilterButton filters={colFilters} colKey="type" label="Type" /></th>
                      <th style={th} className="th-filterable" title="Click to open the invoice">Invoice No<ColumnFilterButton filters={colFilters} colKey="invoice" label="Invoice No" /></th>
                      <th style={th}>Details</th>
                      <th style={{ ...th, textAlign: 'right' }} title="Sales, purchases, commission and bills. Credit notes and returns take it back (minus).">Invoiced</th>
                      <th style={{ ...th, textAlign: 'right' }} title="Payments and advances, in or out">Paid / Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.pageRows.map(r => {
                      const side = historySide(r.kind);
                      const value = side.sign * r.amount;
                      return (
                        <tr key={r.key}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{shortDate(r.date)}</td>
                          <td><KindPill row={r} /></td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {canOpen(r) ? (
                              <button type="button" className="eh-inv-link" title={`Open ${r.ref}`}
                                onClick={() => openInvoice(r)} disabled={opening === r.key}>
                                {shortInvoiceNo(r.ref)}
                              </button>
                            ) : r.ref ? <span title={r.ref} style={{ ...td, fontFamily: 'var(--font-num)' }}>{shortInvoiceNo(r.ref)}</span> : muted}
                          </td>
                          <td style={td}>{r.detail || muted}</td>
                          <td style={{ ...num, color: value < 0 ? 'var(--danger)' : td.color }}>{side.col === 'invoiced' ? money(value) : muted}</td>
                          <td style={num}>{side.col === 'paid' ? money(value) : muted}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ ...td, fontWeight: 700, color: 'var(--text-1)' }}>
                        Total{hist.totalPages > 1 ? ` (all ${shown.length} rows)` : ''}
                      </td>
                      <td style={{ ...num, fontWeight: 700, color: 'var(--text-1)' }}>{money(totals.invoiced)}</td>
                      <td style={{ ...num, fontWeight: 700, color: 'var(--text-1)' }}>{money(totals.paid)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <Paging page={hist.page} totalPages={hist.totalPages} total={hist.total} onChange={hist.setPage} noun="row" />
            </>
          )) : (memos.length === 0 ? (
            <div className="card" style={{ padding: '22px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              {allMemos.length === 0 ? 'Nothing out on memo with this company.' : 'Nothing matches these filters.'}
            </div>
          ) : (
            <>
              <div ref={fitRef} className="table-wrap table-fit table-fit-stats">
                  <table>
                    <thead>
                      <tr>
                        <th>Sent</th>
                        <th className="th-filterable">Code<ColumnFilterButton filters={memoFilters} colKey="code" label="Code" /></th>
                        <th className="th-filterable">Stone<ColumnFilterButton filters={memoFilters} colKey="stone" label="Stone" /></th>
                        <th style={{ textAlign: 'right' }}>Carat</th>
                        <th style={{ textAlign: 'right' }}>Price/ct</th>
                        <th style={{ textAlign: 'right' }}>Value</th>
                        <th className="th-filterable">Return by<ColumnFilterButton filters={memoFilters} colKey="returnBy" label="Return by" align="right" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {memoPg.pageRows.map(m => {
                        const late = !!m.returnBy && m.returnBy < today;
                        return (
                          <tr key={m.key}>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>{shortDate(m.date)}</td>
                            <td>{m.code ? <RefPill value={m.code} /> : muted}</td>
                            <td style={td}>{m.stone || muted}</td>
                            <td style={num}>{m.carat.toFixed(2)}</td>
                            <td style={num}>{m.pricePerCt ? money(m.pricePerCt) : muted}</td>
                            <td style={num}>{m.value ? money(m.value) : muted}</td>
                            <td style={{ ...td, whiteSpace: 'nowrap', color: late ? 'var(--danger)' : td.color, fontWeight: late ? 600 : undefined }}>
                              {m.returnBy ? `${shortDate(m.returnBy)}${late ? ' (late)' : ''}` : muted}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ ...td, fontWeight: 700 }}>Total</td>
                        <td style={{ ...num, fontWeight: 700 }}>{memos.reduce((s, m) => s + m.carat, 0).toFixed(2)}</td>
                        <td />
                        <td style={{ ...num, fontWeight: 700 }}>{money(memos.reduce((s, m) => s + m.value, 0))}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
              </div>
              <Paging page={memoPg.page} totalPages={memoPg.totalPages} total={memoPg.total} onChange={memoPg.setPage} noun="stone" />
            </>
          ))}
        </>
      )}

      <SalePreviewModal invoice={openSale} entityName={entityName} onClose={() => setOpenSale(null)} />
      <InvoicePreviewModal invoice={openBuy} entityName={entityName} onClose={() => setOpenBuy(null)} />
    </AppLayout>
  );
}
